/**
 * useAchievements — tracks and awards badges based on file completion patterns.
 *
 * Checks file history against achievement criteria and returns
 * newly earned badges. Achievements persist in localStorage and
 * reset automatically each calendar month — badges must be re-earned.
 * Streaks (consecutive behaviour) span all time and only reset when broken.
 *
 * Initial achievement set:
 *   Fast Start, Speedy Gonzales, Bullseye, Bronze/Silver/Gold Champion,
 *   On Fire, Clean Slate, First File, Perfect Month
 */

import { useState, useEffect, useMemo, useCallback } from 'react';

// Achievement definitions
const ACHIEVEMENTS = [
  {
    id: 'first_file',
    name: 'First File',
    emoji: '🎓',
    description: 'Complete your first file this month',
    check: (stats) => stats.monthCompleted >= 1,
  },
  {
    id: 'fast_start',
    name: 'Fast Start',
    emoji: '🚀',
    description: 'Complete a file within 1 active day',
    check: (stats) => stats.hasCompletedWithin1DayThisMonth,
  },
  {
    id: 'speed_demon',
    name: 'Speedy Gonzales',
    emoji: '⚡',
    description: '3 consecutive files completed within 1 day',
    check: (stats) => stats.consecutiveWithin1Day >= 3,
  },
  {
    id: 'bullseye',
    name: 'Bullseye',
    emoji: '🎯',
    description: '5 consecutive files within SLA',
    check: (stats) => stats.consecutiveWithinSLA >= 5,
  },
  {
    id: 'bronze_champion',
    name: 'Bronze Champion',
    emoji: '🏆',
    description: 'Reach 20 qualifying files in a month',
    check: (stats) => stats.monthlyQualifying >= 20,
  },
  {
    id: 'silver_star',
    name: 'Silver Star',
    emoji: '🌟',
    description: 'Reach 25 qualifying files in a month',
    check: (stats) => stats.monthlyQualifying >= 25,
  },
  {
    id: 'gold_standard',
    name: 'Gold Standard',
    emoji: '👑',
    description: 'Reach 30 qualifying files in a month',
    check: (stats) => stats.monthlyQualifying >= 30,
  },
  {
    id: 'on_fire',
    name: 'On Fire',
    emoji: '🔥',
    description: '10-file processing streak within SLA',
    check: (stats) => stats.consecutiveWithinSLA >= 10,
  },
  {
    id: 'clean_slate',
    name: 'Clean Slate',
    emoji: '📋',
    description: 'Clear all inbox files in a single day',
    check: (stats) => stats.clearedInboxToday,
  },
  {
    id: 'perfect_month',
    name: 'Perfect Month',
    emoji: '💎',
    description: 'All files in a month completed within SLA',
    check: (stats) => stats.perfectMonth,
  },
];

// Helper to get current month in YYYY-MM format
function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * @param {Array} files — all file records
 * @param {string} userEmail — current user's email
 * @returns {object} { earned, newlyEarned, checkAchievements, allAchievements }
 */
export default function useAchievements(files, userEmail) {
  const currentMonth = getCurrentMonth();

  // Load earned badges, filtering to current month only
  const [earned, setEarned] = useState(() => {
    const saved = localStorage.getItem(`inbox_achievements_${userEmail}`);
    if (!saved) return [];
    const all = JSON.parse(saved);
    return all.filter((e) => e.monthYear === currentMonth);
  });

  const [newlyEarned, setNewlyEarned] = useState([]);

  // Compute stats from file history
  const stats = useMemo(() => {
    // Test records (is_test = true, CLAUDE.md §33) never feed streaks,
    // badges, or the qualifying counts — same contract as the stat cards.
    const realFiles = files.filter((f) => f.isTest !== true);
    const userFiles = realFiles.filter((f) => f.drawnBy === userEmail);
    const completed = userFiles
      .filter((f) => f.status === 'completed')
      .sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));

    // Monthly scoped stats
    const monthFiles = completed.filter((f) => f.monthYear === currentMonth);
    const monthCompleted = monthFiles.length;
    const monthlyQualifying = monthFiles.filter((f) => f.qualifiesForBonus).length;

    // Has completed a file within 1 day this month
    const hasCompletedWithin1DayThisMonth = monthFiles.some((f) => (f.activeDaysUsed || 0) <= 1);

    // Streaks span all time — only reset when broken (not monthly)
    // Consecutive within SLA streak (from most recent backwards)
    let consecutiveWithinSLA = 0;
    for (let i = completed.length - 1; i >= 0; i--) {
      if (!completed[i].slaBreached) {
        consecutiveWithinSLA++;
      } else {
        break;
      }
    }

    // Consecutive within 1 day (from most recent backwards)
    let consecutiveWithin1Day = 0;
    for (let i = completed.length - 1; i >= 0; i--) {
      if ((completed[i].activeDaysUsed || 0) <= 1) {
        consecutiveWithin1Day++;
      } else {
        break;
      }
    }

    // Check if inbox was cleared today
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const submittedToday = realFiles.filter(
      (f) => f.status === 'submitted' && f.submittedAt?.startsWith(todayStr)
    );
    const clearedInboxToday =
      submittedToday.length === 0 && realFiles.filter((f) => f.status === 'submitted').length === 0;

    // Perfect month: all month files completed within SLA (min 5 files)
    const monthBreached = monthFiles.filter((f) => f.slaBreached).length;
    const perfectMonth = monthCompleted >= 5 && monthBreached === 0;

    return {
      monthCompleted,
      monthlyQualifying,
      consecutiveWithinSLA,
      consecutiveWithin1Day,
      hasCompletedWithin1DayThisMonth,
      clearedInboxToday,
      perfectMonth,
    };
  }, [files, userEmail, currentMonth]);

  // Check achievements against current stats (month-aware)
  const checkAchievements = useCallback(() => {
    const newBadges = [];

    for (const achievement of ACHIEVEMENTS) {
      // Only check against current month's earned badges
      const alreadyEarned = earned.some((e) => e.id === achievement.id);
      if (!alreadyEarned && achievement.check(stats)) {
        const badge = {
          id: achievement.id,
          name: achievement.name,
          emoji: achievement.emoji,
          description: achievement.description,
          earnedAt: new Date().toISOString(),
          monthYear: currentMonth,
        };
        newBadges.push(badge);
      }
    }

    if (newBadges.length > 0) {
      // Update current month's earned list
      const updatedCurrentMonth = [...earned, ...newBadges];
      setEarned(updatedCurrentMonth);
      setNewlyEarned(newBadges);

      // Persist all months' badges to localStorage (keep history, display only current)
      const saved = localStorage.getItem(`inbox_achievements_${userEmail}`);
      const allBadges = saved ? JSON.parse(saved) : [];
      const updatedAll = [...allBadges, ...newBadges];
      localStorage.setItem(`inbox_achievements_${userEmail}`, JSON.stringify(updatedAll));
    }

    return newBadges;
  }, [earned, stats, userEmail, currentMonth]);

  // Auto-check when files change
  useEffect(() => {
    if (files.length > 0) {
      checkAchievements();
    }
  }, [files, checkAchievements]);

  // Clear newly earned after it's been read
  const clearNewlyEarned = useCallback(() => {
    setNewlyEarned([]);
  }, []);

  return {
    earned,
    newlyEarned,
    clearNewlyEarned,
    checkAchievements,
    allAchievements: ACHIEVEMENTS,
    stats,
  };
}
